import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import {
  CdkCell,
  CdkCellDef,
  CdkColumnDef,
  CdkHeaderCell,
  CdkHeaderCellDef,
  CdkHeaderRow,
  CdkHeaderRowDef,
  CdkRow,
  CdkRowDef,
  CdkTable
} from "@angular/cdk/table";
import { OpportunityService } from "../../../services/opportunity.service";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faCircle as faCircleRegular } from "@fortawesome/pro-regular-svg-icons/faCircle";
import { faCircleX } from "@fortawesome/pro-solid-svg-icons/faCircleX";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { DatePipe, NgClass, NgIf } from "@angular/common";
import { DateAgoPipe } from "../../../pipes/date-ago.pipe";
import { AllReferralDatasource } from "../../../utility/all-referral-datasource";
import {
  faCircle,
  faGear,
  faInfoCircle,
  faLocationDot,
  faPhone
} from "@fortawesome/pro-solid-svg-icons";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons/faCircleCheck";
import { AuthGuard } from "../../../auth/auth.guard";
import { DropdownComponent } from "../../dropdown/dropdown.component";
import { ClaimItReferral } from "../../../models/claim-it-referral.model";
import { environment } from "../../../../environments/environment";
import { Environment } from "../../../../environments/environment.model";
import { DetailsPanelComponent } from "../../details-panel/details-panel.component";
import { UtilityService } from "../../../services/utility.service";
import { DropdownType } from "../../../models/dropdown-type.enum";
import { Option } from "../../../models/option.model";
import { FilterOptions } from "../../../utility/filter-options";
import { PublicClaimModalComponent } from "../../public-claim-modal/public-claim-modal.component";
import { Subject } from "rxjs";
import { LocationService } from "../../../services/location.service";
import { Location } from "../../../models/location.model";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { StateManagementService } from "../../../services/state-management.service";
import { CustomCdkTooltipDirective } from "../../../directives/custom-cdk-tool-tip.directive";

@Component({
  selector: "app-all-referrals",
  standalone: true,
  imports: [
    CdkCell,
    CdkTable,
    CdkColumnDef,
    CdkHeaderCell,
    CdkHeaderRow,
    CdkRow,
    FaIconComponent,
    CdkCellDef,
    CdkHeaderCellDef,
    NgIf,
    CdkHeaderRowDef,
    CdkRowDef,
    NgClass,
    DatePipe,
    DateAgoPipe,
    DropdownComponent,
    DateAgoPipe,
    DetailsPanelComponent,
    PublicClaimModalComponent,
    CustomCdkTooltipDirective
  ],
  templateUrl: "./all-referrals.component.html",
  styleUrl: "./all-referrals.component.scss"
})
export class AllReferralsComponent implements OnInit, OnDestroy {
  @Input() loadGeoIpStateFilter: boolean = false;
  @Input() enableDetailsPanel: boolean = true;
  @Input() showDateFilter: boolean = true;
  @Input() showStateFilter: boolean = false;
  @Input() showHeader: boolean = true;
  @Input() pageTitle: string | null = null;
  @Input() showPrices: boolean = true;
  @Input() selectedStateCodeFilter: string | null = null;
  @Input() selectedDateFilter: string | null = "48hours";
  @Input() defaultDateOptionIndex: number = 1;
  @Input() selectedIndex: number | null = null;
  private readonly isPrivileged = this.authGuard?.isAdmin?.() || this.authGuard?.isCarrier?.();
  @Input() columnsToDisplay: string[] = !this.isPrivileged
    ? [
        "addDate",
        "decayStatus",
        "limitedTime",
        "category",
        "city",
        "businessLines",
        "industry",
        "noOfEmployees",
        "yearsInBusiness",
        "estimatedPremium",
        "assignedAgency",
        "isMobileView"
      ]
    : [
        "addDate",
        "type",
        "decayStatus",
        "limitedTime",
        "category",
        "city",
        "businessLines",
        "industry",
        "noOfEmployees",
        "yearsInBusiness",
        "estimatedPremium",
        "agenciesNotified",
        "assignedAgency",
        "isMobileView"
      ];
  @Input() sortField: string = this.isPrivileged ? "addDate" : "marketplaceExpirationDate";
  @Input() sortOrder: string = this.isPrivileged ? "desc" : "asc";
  @Input() showClaimed: boolean = this.isPrivileged;
  @Input() showExpiration: boolean = true;
  protected readonly faGear: IconDefinition = faGear;
  protected readonly faPhone: IconDefinition = faPhone;
  protected readonly faLocationDot: IconDefinition = faLocationDot;
  protected readonly environment: Environment = environment;
  protected readonly DropdownType: typeof DropdownType = DropdownType;
  protected readonly FilterOptions: typeof FilterOptions = FilterOptions;
  protected readonly UtilityService: typeof UtilityService = UtilityService;
  protected readonly faInfoCircle: IconDefinition = faInfoCircle;
  protected readonly CRITICAL_MINUTES_THRESHOLD = 5;
  protected readonly MINIMUM_MINUTES_DISPLAY = 1;
  private filters: { [key: string]: string } = {};
  protected selectedReferral: ClaimItReferral | null = null;
  protected username: string | null = null;
  protected dataSource!: AllReferralDatasource;
  protected icons: { [id: string]: IconDefinition } = {
    Assigned: faCircleCheck,
    Claimed: faCircleCheck,
    Available: faCircle,
    Settled: faCircleCheck,
    Incoming: faCircleRegular,
    Ineligible: faCircleX,
    Delivered: faCircleCheck,
    Undefined: faCircleX
  };
  protected iconColor: { [id: string]: string } = {
    Assigned: "text-slate-500",
    Claimed: "text-slate-500",
    Available: "text-momentum-royal_purple",
    Settled: "text-slate-500",
    Incoming: "text-slate-500",
    Ineligible: "text-slate-500",
    Delivered: "text-slate-500"
  };
  protected statusDisplay: { [id: string]: string } = {
    Assigned: "Assigned",
    Claimed: "Claimed",
    Available: "Available",
    Settled: "Settled",
    Incoming: "Incoming",
    Ineligible: "Ineligible",
    Delivered: "Delivered"
  };
  protected isLoading: boolean = true;
  protected openModal$: Subject<void> = new Subject<void>();

  constructor(
    private opportunityService: OpportunityService,
    protected authGuard: AuthGuard,
    public stateManagementService: StateManagementService,
    private locationService: LocationService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const routeSnapshotData = this.route?.snapshot?.data ?? {};
    if (this.showClaimed === undefined || this.showClaimed === null) {
      this.showClaimed = routeSnapshotData["showClaimed"] ?? this.showClaimed ?? false;
    }

    if (this.pageTitle) {
      this.stateManagementService.setPageTitle(this.pageTitle);
    } else {
      this.stateManagementService.setPageTitle("Claim-it Marketplace | Momentum Edge");
    }

    this.dataSource = new AllReferralDatasource(
      this.opportunityService,
      this.sortField,
      this.sortOrder,
      0,
      25,
      this.filters,
      this.username,
      this.showClaimed,
      this.showExpiration,
      6000
    );

    this.stateManagementService.pageHeading.set("Claim-it Marketplace");
    this.loadUsername();
    this.loadInitialDateFilter();
    this.loadInitialStateFilter();
  }

  ngOnDestroy(): void {
    this.stateManagementService.resetHeadingAndTitle();
    this.dataSource.disconnect();
  }

  openModal(): void {
    if (!this.enableDetailsPanel) {
      this.openModal$.next();
    }
  }

  finishedLoading(): void {
    setTimeout((): boolean => (this.isLoading = false));
  }

  loadUsername(): void {
    this.authGuard.fetchUsername().subscribe((username: string | null): void => {
      this.username = username;
      if (!this.authGuard.isAdmin()) {
        this.dataSource.setUsername(username);
      }
    });
  }

  loadInitialStateFilter(): void {
    // Check to see if StateCodeFilter is set via URL
    this.route.queryParams.subscribe((params: Params): void => {
      if (params.hasOwnProperty("state")) {
        this.selectedStateCodeFilter =
          params["state"].length === 2 ? params["state"].toUpperCase() : null;
        this.loadStateFilter();
      } else {
        this.loadStateFilter();
      }
    });
  }

  private loadStateFilter(): void {
    if (this.loadGeoIpStateFilter) {
      this.locationService.getLocation().subscribe({
        next: (location: Location) =>
          this.handleOptionSelected(
            "state",
            FilterOptions.getStateFilterOption(this.getGeoIpStateCode(location))
          ),
        error: () =>
          this.handleOptionSelected(
            "state",
            FilterOptions.getStateFilterOption(this.selectedStateCodeFilter)
          )
      });
    } else {
      this.handleOptionSelected(
        "state",
        FilterOptions.getStateFilterOption(this.selectedStateCodeFilter)
      );
    }
  }

  loadInitialDateFilter(): void {
    this.handleOptionSelected(
      "addDate",
      FilterOptions.getDateFilterOption(this.selectedDateFilter)
    );
  }

  getGeoIpStateCode(location: Location): string | null {
    if (
      location == null ||
      location.state == null ||
      FilterOptions.getStateFilterOption(location.state) == null
    ) {
      return this.selectedStateCodeFilter;
    } else {
      return location.state;
    }
  }

  handleOptionSelected(columnName: string, option: Option | null): void {
    if (columnName === "state") {
      this.selectedStateCodeFilter = option == null ? null : option.field;
      this.updateUrlStateParam();
    } else if (columnName === "addDate") {
      this.selectedDateFilter = option == null ? null : option.field;
    }
    this.dataSource.filter(FilterOptions.buildFilters(columnName, option, this.filters));
  }

  updateUrlStateParam(): void {
    this.router
      .navigate([this.router.url], {
        queryParams: { state: this.selectedStateCodeFilter },
        queryParamsHandling: "merge"
      })
      .finally();
  }

  setSelectedReferral(referral: any, index: number | null): void {
    if (!this.enableDetailsPanel) {
      return;
    }
    if (this.selectedIndex === index) {
      // Toggles the details panel:
      // - Closes it if the same row is clicked again
      // - Allows Angular to reopen the details panel if it's the only row in the report

      this.selectedReferral = null;
      this.selectedIndex = null;
    } else {
      this.selectedReferral = referral;
      this.selectedIndex = index;
    }
  }

  getPersonalOrLifeShortText(category: any): string | null {
    const isMobileScreen = window.innerWidth <= 768;

    if (category === null) {
      return null;
    }

    if (
      isMobileScreen &&
      (category.toLowerCase() === "personal" || category.toLowerCase() === "life and annuities")
    ) {
      return "";
    }

    if (category.toLowerCase() === "personal") {
      return "N/A (PL)";
    }

    if (category.toLowerCase() === "life and annuities") {
      return "N/A (L&A)";
    }

    return null;
  }

  getAssigneeText(
    assignedAgent: string,
    assignedAgency: string,
    assignedPartner: string
  ): string | null {
    if (assignedAgency != null) {
      if (assignedAgent != null) {
        return `${assignedAgent}, ${assignedAgency}`;
      }
      return assignedAgency;
    }

    if (assignedPartner != null) {
      if (this.authGuard.isAdmin()) {
        return assignedPartner;
      } else {
        return "Secondary Partner";
      }
    }

    return null;
  }

  isMomentumEdge(): boolean {
    const domain = this.getDomainWithoutTld();
    return domain === "trustedchoice" || domain === "momentumedge";
  }

  isAgentium(): boolean {
    return !this.isMomentumEdge();
  }

  getDomainWithoutTld(): string | null {
    let fullHostnameArray: string[] = window.location.hostname.split(".");

    if (fullHostnameArray == null || fullHostnameArray.length === 0) {
      return null;
    }

    if (fullHostnameArray.length === 1) {
      return fullHostnameArray[0];
    }

    return fullHostnameArray[fullHostnameArray.length - 2];
  }

  getMinutesLeft(marketplaceExpirationDate: string | Date): number {
    const now = new Date();
    const expiration = new Date(marketplaceExpirationDate);
    const diffMs = expiration.getTime() - now.getTime();
    const diffMinutes = Math.ceil(diffMs / 60000); // 60,000 ms in 1 minute
    console.log(now, expiration, diffMs, diffMinutes, this.MINIMUM_MINUTES_DISPLAY);
    return Math.max(diffMinutes, this.MINIMUM_MINUTES_DISPLAY);
  }

  getMinutesLeftLabel(minutesLeft: number): string {
    const displayMinutes = Math.max(minutesLeft, this.MINIMUM_MINUTES_DISPLAY);
    const plural = displayMinutes === 1 ? "" : "s";
    return `${displayMinutes} minute${plural} left`;
  }

  getMinutesLeftClass(minutesLeft: number): string {
    return minutesLeft <= this.CRITICAL_MINUTES_THRESHOLD ? "text-red-600" : "text-slate-600";
  }

  hasPriceDecay(referral: any): boolean {
    return (
      referral.discountPrice != null &&
      referral.dynamicPrice != null &&
      referral.discountPrice > referral.dynamicPrice
    );
  }

  onHeaderClick(field: "addDate" | "marketplaceExpirationDate") {
    const switching = this.sortField !== field;
    const defaultDir = field === "marketplaceExpirationDate" ? "asc" : "desc";
    this.sortOrder = switching ? defaultDir : this.sortOrder === "asc" ? "desc" : "asc";
    this.sortField = field;

    this.dataSource.setSortField(field);
  }
}
